/**
 * The rules for putting a pasted image on a board.
 *
 * Extracted from board-canvas.tsx for the same reason board-select.ts was: the
 * part that decides WHAT happens is checkable without a browser, and nothing in
 * this repo can drive a real paste. `scripts/board-image-check.mjs` asserts
 * every function here.
 *
 * The storage half lives in `migrations/0043_board_images.sql`, and the two
 * have to agree on three things: the accepted types, the size ceiling, and the
 * path layout. All three are stated once, here, and the migration repeats them
 * in SQL because a bucket cannot import a TypeScript constant.
 */

/**
 * What the bucket accepts. Kept in step with `allowed_mime_types` in 0043.
 *
 * SVG is deliberately absent: it is a document rather than a picture, and one
 * carrying a <script> is a problem the moment anything renders it outside an
 * <img>. Nobody pastes an SVG from a screenshot tool anyway.
 */
export const IMAGE_MIME_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'] as const

/** Matches `file_size_limit` on the bucket. A full-screen retina PNG fits. */
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024

/** How wide a pasted image lands on the canvas, before anyone resizes it. */
export const PASTED_MAX_EDGE = 480

interface PastedFile {
  type: string
  size: number
}

const EXTENSIONS: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/gif': 'gif',
  'image/webp': 'webp',
}

/** The extension for a type the bucket accepts, or null for one it does not. */
export function imageExtension(mime: string): string | null {
  return EXTENSIONS[mime] ?? null
}

/**
 * The first usable image in a paste, or null.
 *
 * A paste from a screenshot tool carries one file. A paste from a web page
 * carries an image AND the HTML around it, and a paste from a file manager can
 * carry several files at once — so this picks rather than asserts, and takes
 * the first thing it can actually store.
 */
export function pickImageFile<T extends PastedFile>(files: readonly T[]): T | null {
  return files.find((f) => imageExtension(f.type) !== null) ?? null
}

/**
 * Why this file cannot go on the board, as a sentence, or null if it can.
 *
 * Returned rather than thrown: a paste that quietly does nothing is the worst
 * outcome here, because the person cannot tell it from a board that is broken.
 */
export function imageRejection(file: PastedFile): string | null {
  if (imageExtension(file.type) === null) {
    return `Boards take PNG, JPEG, GIF and WebP images. That one is ${file.type || 'of an unknown type'}.`
  }
  if (file.size > MAX_IMAGE_BYTES) {
    const mb = (file.size / 1024 / 1024).toFixed(1)
    return `That image is ${mb}MB. The limit is ${MAX_IMAGE_BYTES / 1024 / 1024}MB — crop it, or save it as a JPEG.`
  }
  if (file.size === 0) return 'That image is empty.'
  return null
}

/**
 * Where the bytes live: `<team_id>/<board_id>/<random>.<ext>`.
 *
 * The team comes FIRST because the storage policies are a prefix check on it —
 * see 0043. Moving it out of the first segment silently unscopes the bucket, so
 * `scripts/board-image-check.mjs` asserts the shape and `e2e-check.mjs` asserts
 * that the database refuses a path without it.
 */
export function storagePath(teamId: string, boardId: string, mime: string, name: string): string {
  const ext = imageExtension(mime)
  if (!ext) throw new Error(`unsupported image type: ${mime}`)
  return `${teamId}/${boardId}/${name}.${ext}`
}

/**
 * The size the image lands at: its own, shrunk to fit a 480px box.
 *
 * A screenshot is often 3000px wide. Dropping it at full size covers the board
 * and every sticky on it, and the person's first act has to be a resize. A
 * small image is left alone rather than blown up, because a 40px icon pasted as
 * a 480px blur helps nobody.
 */
export function fitDisplaySize(
  naturalWidth: number,
  naturalHeight: number,
  maxEdge = PASTED_MAX_EDGE,
): { width: number; height: number } {
  // A zero or missing dimension means the browser could not measure it. Fall
  // back to a square, which is wrong but visible and resizable.
  if (!naturalWidth || !naturalHeight || naturalWidth < 0 || naturalHeight < 0) {
    return { width: maxEdge, height: maxEdge }
  }
  const scale = Math.min(1, maxEdge / Math.max(naturalWidth, naturalHeight))
  return {
    width: Math.round(naturalWidth * scale),
    height: Math.round(naturalHeight * scale),
  }
}

/** What an image object keeps in `extra`. The path is the only required part. */
export interface ImageExtra {
  path: string
  mime?: string
  bytes?: number
  naturalWidth?: number
  naturalHeight?: number
}

/**
 * The storage path on a board object, or null when there is not one.
 *
 * `extra` is jsonb, so it is `Record<string, unknown>` to TypeScript and could
 * hold anything at all — including rows written by an older version of this
 * page. Everything that renders an image goes through here.
 */
export function imagePathOf(extra: Record<string, unknown> | null | undefined): string | null {
  const path = extra?.path
  return typeof path === 'string' && path.length > 0 ? path : null
}
